import React, { useState, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Tab,
    Tabs,
    Switch,
    FormControlLabel,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
} from '@mui/material';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CloseIcon from '@mui/icons-material/Close';
import PreviewIcon from '@mui/icons-material/Preview';
import SettingsIcon from '@mui/icons-material/Settings';
import CreateIcon from '@mui/icons-material/Create';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { v4 as uuidv4 } from 'uuid';
import {
    IRegistrationFormField,
    ISelectOption,
    FormOption,
    FormFieldType,
    FieldType,
} from '../../types/global/forms.types';
import { FormGenerator } from '../FormGenerator/FormGenerator';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { useThemeContext } from '../../hook/useThemeManager';

// Custom TabPanel component
interface TabPanelProps {
    children?: React.ReactNode;
    value: number;
    index: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
    return (
        <div role="tabpanel" hidden={value !== index} style={{ height: '100%' }}>
            {value === index && <Box sx={{ height: '100%' }}>{children}</Box>}
        </div>
    );
};

// Form element type
type FormElementType = FormFieldType;

// Get default options for field types that support options
const getDefaultOptionsForType = (type: FormElementType): FormOption[] => {
    switch (type) {
        case 'dropdown':
        case 'pillSelection':
        case 'radioSelection':
            return [
                { label: 'Option 1', value: 'option_1' },
                { label: 'Option 2', value: 'option_2' },
                { label: 'Option 3', value: 'option_3' },
            ];
        default:
            return [];
    }
};

interface ElementTypeOption {
    type: FormElementType;
    label: string;
    description: string;
    icon?: React.ReactNode;
}

interface FormCreatorProps {
    initialFields?: IRegistrationFormField[];
    onChange?: (fields: IRegistrationFormField[]) => void;
    onSave?: (formFields: IRegistrationFormField[]) => void;
}

// Available element types
const ELEMENT_TYPES: ElementTypeOption[] = [
    { type: 'textInput', label: 'Text Input', description: 'Single line text input' },
    { type: 'dropdown', label: 'Dropdown', description: 'Select from a list of options' },
    { type: 'pillSelection', label: 'Pill Selection', description: 'Select options as pills' },
    { type: 'radioSelection', label: 'Radio Selection', description: 'Select one option from a list' },
    { type: 'calendar', label: 'Date Picker', description: 'Pick a date' },
    { type: 'phone', label: 'Phone Number', description: 'International phone number input' },
    { type: 'location', label: 'Location', description: 'Select a location' },
    { type: 'slider', label: 'Slider', description: 'Select a value with a slider' },
    { type: 'range', label: 'Range Slider', description: 'Select a range of values' },
    { type: 'currency', label: 'Currency', description: 'Input with currency selection' },
    { type: 'picture', label: 'Image Upload', description: 'Upload image files' },
    { type: 'video', label: 'Video Upload', description: 'Upload video files' },
    { type: 'upload', label: 'File Upload', description: 'Upload any file type' },
    { type: 'displayText', label: 'Display Text', description: 'Show static text in the form' },
    { type: 'suggestionTextInput', label: 'Suggestions', description: 'Text input with suggestions' },
    { type: 'mediaAdder', label: 'Media Selection', description: 'Select media from your library' },
];

// Form element mapper to convert between FormFieldType and FieldType
const formFieldTypeToFieldType = (formFieldType: FormFieldType): FieldType => {
    switch (formFieldType) {
        case 'textInput':
            return 'text';
        case 'dropdown':
            return 'select';
        case 'pillSelection':
            return 'checkbox';
        case 'radioSelection':
            return 'radio';
        case 'phone':
            return 'phone';
        case 'calendar':
            return 'date';
        default:
            return 'text';
    }
};

const FormCreator: React.FC<FormCreatorProps> = ({ initialFields = [], onChange, onSave }) => {
    const { themeStyle } = useThemeContext();
    const [elements, setElements] = useState<IRegistrationFormField[]>(initialFields.map(field => ({ ...field })));
    const [showElementPicker, setShowElementPicker] = useState(false);
    const [selectedTab, setSelectedTab] = useState<number>(0);

    // Element Settings Dialog
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [currentElementIndex, setCurrentElementIndex] = useState<number | null>(null);
    const [editElement, setEditElement] = useState<IRegistrationFormField | null>(null);
    const [newOption, setNewOption] = useState<FormOption>({ label: '', value: '' });

    // Create a UUID for elements
    const createUUID = () => {
        return uuidv4();
    };

    // Add a new element
    const addElement = (type: FormElementType) => {
        const formOptions = getDefaultOptionsForType(type);
        const newElement: IRegistrationFormField = {
            _id: createUUID(),
            id: createUUID(),
            field: `New ${type}`,
            form_type: type,
            form_name: `New ${type}`,
            store_key: createUUID(),
            form_header: '',
            required: false,
            form_options: formOptions,
            position: elements.length,
            deleted: false,
            __v: 0,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            type: formFieldTypeToFieldType(type),
            options: mapFormOptionsToSelectOptions(formOptions),
        };
        setElements([...elements, newElement]);
        setShowElementPicker(false);
        if (onChange) onChange([...elements, newElement]);
    };

    // Remove an element
    const removeElement = (index: number) => {
        const newElements = [...elements];
        newElements.splice(index, 1);
        // Update positions
        const updatedElements = newElements.map((el, idx) => ({ ...el, position: idx }));
        setElements(updatedElements);
        if (onChange) onChange(updatedElements);
    };

    // Handle drag end
    const handleDragEnd = useCallback(
        (result: DropResult) => {
            const { destination, source } = result;

            if (!destination) return;

            if (destination.droppableId === source.droppableId && destination.index === source.index) {
                return;
            }

            const newFields = Array.from(elements);
            const [removed] = newFields.splice(source.index, 1);
            newFields.splice(destination.index, 0, removed);

            // Update positions
            const updatedFields = newFields.map((field, index) => ({
                ...field,
                position: index,
            }));

            setElements(updatedFields);
            if (onChange) onChange(updatedFields);
        },
        [elements, onChange],
    );

    // Open settings dialog
    const openSettings = (index: number) => {
        setCurrentElementIndex(index);
        setEditElement({ ...elements[index] });
        setIsSettingsOpen(true);
    };

    // Save element settings
    const saveSettings = () => {
        if (editElement && currentElementIndex !== null) {
            const newFields = [...elements];
            newFields[currentElementIndex] = editElement;
            setElements(newFields);
            setIsSettingsOpen(false);
            setEditElement(null);
            setCurrentElementIndex(null);
            if (onChange) onChange(newFields);
        }
    };

    // Map form options to select options
    const mapFormOptionsToSelectOptions = (formOptions: FormOption[]): ISelectOption[] => {
        return formOptions.map(option => ({
            label: option.label,
            value: option.value,
        }));
    };

    // Add option to element
    const addOption = useCallback(() => {
        if (currentElementIndex === null || !editElement || !editElement.form_options) return;

        const newOptions = [...editElement.form_options];
        newOptions.push(newOption);

        setEditElement({
            ...editElement,
            form_options: newOptions,
            options: mapFormOptionsToSelectOptions(newOptions),
        });

        setNewOption({ label: '', value: '' });
    }, [currentElementIndex, editElement, newOption]);

    // Remove option from element
    const removeOption = (index: number) => {
        if (currentElementIndex === null || !editElement || !editElement.form_options) return;

        const newOptions = [...editElement.form_options];
        newOptions.splice(index, 1);

        setEditElement({
            ...editElement,
            form_options: newOptions,
            options: mapFormOptionsToSelectOptions(newOptions),
        });
    };

    // Convert to form generator format
    const convertToFormGeneratorFormat = (): IRegistrationFormField[] => {
        return elements;
    };

    // Handle save form
    const handleSaveForm = () => {
        if (onSave) {
            onSave(elements);
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden',
                bgcolor: theme[themeStyle].form_background,
            }}>
            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                    value={selectedTab}
                    onChange={(_, newValue) => setSelectedTab(newValue)}
                    sx={{
                        '& .MuiTabs-indicator': {
                            backgroundColor: theme[themeStyle].primary_orange,
                        },
                    }}>
                    <Tab
                        icon={<CreateIcon />}
                        iconPosition="start"
                        label="Edit"
                        sx={{
                            color:
                                selectedTab === 0 ? theme[themeStyle].primary_orange : theme[themeStyle].primary_white,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            textTransform: 'none',
                        }}
                    />
                    <Tab
                        icon={<VisibilityIcon />}
                        iconPosition="start"
                        label="Preview"
                        sx={{
                            color:
                                selectedTab === 1 ? theme[themeStyle].primary_orange : theme[themeStyle].primary_white,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            textTransform: 'none',
                        }}
                    />
                </Tabs>
            </Box>

            {/* Edit Tab */}
            <TabPanel value={selectedTab} index={0}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: 'calc(100% - 40px)',
                        overflow: 'auto',
                        p: 2,
                    }}>
                    {/* Add Element Button */}
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setShowElementPicker(!showElementPicker)}
                        sx={{
                            mb: 2,
                            bgcolor: theme[themeStyle].primary_orange,
                            '&:hover': {
                                bgcolor: theme[themeStyle].highlight,
                            },
                            alignSelf: 'flex-start',
                        }}>
                        Add Element
                    </Button>

                    {/* Element Picker */}
                    {showElementPicker && (
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                                gap: 2,
                                mb: 3,
                            }}>
                            {ELEMENT_TYPES.map(type => (
                                <Box
                                    key={type.type}
                                    onClick={() => addElement(type.type)}
                                    sx={{
                                        p: 2,
                                        border: `1px solid ${theme[themeStyle].darkGray60}`,
                                        borderRadius: 1,
                                        cursor: 'pointer',
                                        '&:hover': {
                                            borderColor: theme[themeStyle].primary_orange,
                                        },
                                        textAlign: 'center',
                                    }}>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            color: theme[themeStyle].primary_white,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        }}>
                                        {type.label}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: theme[themeStyle].darkGray60,
                                            display: 'block',
                                            mt: 1,
                                        }}>
                                        {type.description}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {/* Form Elements List */}
                    <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId="form-elements">
                            {provided => (
                                <List
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    sx={{
                                        bgcolor: theme[themeStyle].secondaryBg,
                                        borderRadius: 1,
                                        flexGrow: 1,
                                        overflow: 'auto',
                                    }}>
                                    {elements.length === 0 ? (
                                        <Box
                                            sx={{
                                                height: '100%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: theme[themeStyle].primary_white,
                                                p: 4,
                                            }}>
                                            <Typography variant="body1">
                                                No elements added yet. Add an element to get started.
                                            </Typography>
                                        </Box>
                                    ) : (
                                        elements.map((element, index) => (
                                            <Draggable key={element.id} draggableId={element.id} index={index}>
                                                {(provided, snapshot) => (
                                                    <ListItem
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        sx={{
                                                            mb: 1,
                                                            p: 2,
                                                            bgcolor: theme[themeStyle].darkGray60,
                                                            borderRadius: 1,
                                                            border: `1px solid ${
                                                                snapshot.isDragging
                                                                    ? theme[themeStyle].primary_orange
                                                                    : theme[themeStyle].darkGray60
                                                            }`,
                                                        }}>
                                                        <Box
                                                            {...provided.dragHandleProps}
                                                            sx={{ mr: 2, cursor: 'grab' }}>
                                                            <DragIndicatorIcon
                                                                sx={{ color: theme[themeStyle].darkGray60 }}
                                                            />
                                                        </Box>
                                                        <ListItemText
                                                            primary={
                                                                <Typography
                                                                    sx={{
                                                                        color: theme[themeStyle].primary_white,
                                                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                                    }}>
                                                                    {element.field || `New ${element.type}`}
                                                                </Typography>
                                                            }
                                                            secondary={
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        color: theme[themeStyle].darkGray60,
                                                                        display: 'block',
                                                                        mt: 0.5,
                                                                    }}>
                                                                    {ELEMENT_TYPES.find(t => t.type === element.type)
                                                                        ?.label || element.type}
                                                                    {element.required && ' (Required)'}
                                                                </Typography>
                                                            }
                                                        />
                                                        <ListItemSecondaryAction>
                                                            <IconButton
                                                                edge="end"
                                                                aria-label="settings"
                                                                onClick={() => openSettings(index)}
                                                                sx={{ color: theme[themeStyle].primary_white, mr: 1 }}>
                                                                <SettingsIcon />
                                                            </IconButton>
                                                            <IconButton
                                                                edge="end"
                                                                aria-label="delete"
                                                                onClick={() => removeElement(index)}
                                                                sx={{ color: theme[themeStyle].primary_white }}>
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </ListItemSecondaryAction>
                                                    </ListItem>
                                                )}
                                            </Draggable>
                                        ))
                                    )}
                                    {provided.placeholder}
                                </List>
                            )}
                        </Droppable>
                    </DragDropContext>

                    {/* Save Button */}
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            onClick={handleSaveForm}
                            disabled={elements.length === 0}
                            sx={{
                                bgcolor: theme[themeStyle].primary_orange,
                                '&:hover': {
                                    bgcolor: theme[themeStyle].highlight,
                                },
                                '&.Mui-disabled': {
                                    bgcolor: theme[themeStyle].darkGray60,
                                    color: theme[themeStyle].darkGray60,
                                },
                            }}>
                            Save Form
                        </Button>
                    </Box>
                </Box>
            </TabPanel>

            {/* Preview Tab */}
            <TabPanel value={selectedTab} index={1}>
                <Box sx={{ height: '100%', overflow: 'auto', p: 2 }}>
                    {elements.length === 0 ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%',
                                bgcolor: theme[themeStyle].darkGray60,
                                borderRadius: 1,
                                padding: 3,
                                textAlign: 'center',
                            }}>
                            <PreviewIcon sx={{ fontSize: 48, color: theme[themeStyle].darkGray60 }} />
                            <Typography
                                variant="h6"
                                sx={{
                                    color: theme[themeStyle].primary_white,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    marginTop: 2,
                                }}>
                                No elements to preview
                            </Typography>
                            <Typography variant="body2" sx={{ color: theme[themeStyle].darkGray60, marginTop: 1 }}>
                                Add elements to your form to see a preview
                            </Typography>
                        </Box>
                    ) : (
                        <FormGenerator
                            open={true}
                            onClose={() => {
                                setSelectedTab(0);
                            }}
                            formFields={convertToFormGeneratorFormat()}
                            header="Form Preview"
                            onSubmit={data => setSelectedTab(0)}
                            manualClose={true}
                        />
                    )}
                </Box>
            </TabPanel>

            {/* Settings Dialog */}
            <Dialog
                open={isSettingsOpen}
                onClose={() => setIsSettingsOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    style: {
                        backgroundColor: theme[themeStyle].form_background,
                        borderRadius: 8,
                    },
                }}>
                <DialogTitle
                    sx={{
                        color: theme[themeStyle].primary_white,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}>
                    <span>Element Settings</span>
                    <IconButton edge="end" color="inherit" onClick={() => setIsSettingsOpen(false)} aria-label="close">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {editElement && (
                        <Box sx={{ pt: 1 }}>
                            <TextField
                                fullWidth
                                label="Field Name"
                                value={editElement.field}
                                onChange={e => setEditElement({ ...editElement, field: e.target.value })}
                                margin="normal"
                                InputLabelProps={{
                                    style: { color: theme[themeStyle].primary_white },
                                }}
                                inputProps={{
                                    style: { color: theme[themeStyle].primary_white },
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: theme[themeStyle].darkGray60,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme[themeStyle].primary_orange,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme[themeStyle].primary_orange,
                                        },
                                    },
                                }}
                            />

                            <TextField
                                fullWidth
                                label="Form Name"
                                value={editElement.form_name}
                                onChange={e => setEditElement({ ...editElement, form_name: e.target.value })}
                                margin="normal"
                                InputLabelProps={{
                                    style: { color: theme[themeStyle].primary_white },
                                }}
                                inputProps={{
                                    style: { color: theme[themeStyle].primary_white },
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: theme[themeStyle].darkGray60,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme[themeStyle].primary_orange,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme[themeStyle].primary_orange,
                                        },
                                    },
                                }}
                            />

                            <TextField
                                fullWidth
                                label="Form Header"
                                value={editElement.form_header || ''}
                                onChange={e => setEditElement({ ...editElement, form_header: e.target.value })}
                                margin="normal"
                                InputLabelProps={{
                                    style: { color: theme[themeStyle].primary_white },
                                }}
                                inputProps={{
                                    style: { color: theme[themeStyle].primary_white },
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: theme[themeStyle].darkGray60,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: theme[themeStyle].primary_orange,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme[themeStyle].primary_orange,
                                        },
                                    },
                                }}
                            />

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={!!editElement.required}
                                        onChange={e => setEditElement({ ...editElement, required: e.target.checked })}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                color: theme[themeStyle].primary_orange,
                                            },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                backgroundColor: theme[themeStyle].primary_orange,
                                            },
                                        }}
                                    />
                                }
                                label={
                                    <Typography
                                        sx={{
                                            color: theme[themeStyle].primary_white,
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        }}>
                                        Required
                                    </Typography>
                                }
                            />

                            {/* Option management for select/multi-select fields */}
                            {(editElement.form_type === 'dropdown' ||
                                editElement.form_type === 'pillSelection' ||
                                editElement.form_type === 'radioSelection') && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="h6">Options</Typography>
                                    <Box sx={{ mb: 2 }}>
                                        {editElement.form_options &&
                                            editElement.form_options.map((option, idx) => (
                                                <Box key={idx} sx={{ display: 'flex', mb: 1 }}>
                                                    <TextField
                                                        label="Label"
                                                        value={option.label}
                                                        onChange={e => {
                                                            const updatedOptions = [...editElement.form_options];
                                                            updatedOptions[idx].label = e.target.value;
                                                            setEditElement({
                                                                ...editElement,
                                                                form_options: updatedOptions,
                                                                options: mapFormOptionsToSelectOptions(updatedOptions),
                                                            });
                                                        }}
                                                        sx={{ mr: 1, flex: 1 }}
                                                    />
                                                    <TextField
                                                        label="Value"
                                                        value={option.value}
                                                        onChange={e => {
                                                            const updatedOptions = [...editElement.form_options];
                                                            updatedOptions[idx].value = e.target.value;
                                                            setEditElement({
                                                                ...editElement,
                                                                form_options: updatedOptions,
                                                                options: mapFormOptionsToSelectOptions(updatedOptions),
                                                            });
                                                        }}
                                                        sx={{ mr: 1, flex: 1 }}
                                                    />
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => removeOption(idx)}
                                                        sx={{ color: theme[themeStyle].darkGray60 }}>
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Box>
                                            ))}
                                    </Box>

                                    {/* Add new option */}
                                    <Box sx={{ display: 'flex', mb: 2 }}>
                                        <TextField
                                            label="Label"
                                            value={newOption.label}
                                            onChange={e => setNewOption({ ...newOption, label: e.target.value })}
                                            sx={{ mr: 1, flex: 1 }}
                                        />
                                        <TextField
                                            label="Value"
                                            value={newOption.value}
                                            onChange={e => setNewOption({ ...newOption, value: e.target.value })}
                                            sx={{ mr: 1, flex: 1 }}
                                        />
                                        <IconButton
                                            size="small"
                                            onClick={addOption}
                                            disabled={!newOption.label || !newOption.value}
                                            sx={{
                                                color: theme[themeStyle].primary_orange,
                                                '&.Mui-disabled': {
                                                    color: theme[themeStyle].darkGray60,
                                                },
                                            }}>
                                            <AddIcon />
                                        </IconButton>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <Box sx={{ padding: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        variant="outlined"
                        onClick={() => setIsSettingsOpen(false)}
                        sx={{
                            color: theme[themeStyle].primary_white,
                            borderColor: theme[themeStyle].primary_white,
                            mr: 1,
                            '&:hover': {
                                borderColor: theme[themeStyle].primary_orange,
                            },
                        }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={saveSettings}
                        sx={{
                            bgcolor: theme[themeStyle].primary_orange,
                            '&:hover': {
                                bgcolor: theme[themeStyle].highlight,
                            },
                        }}>
                        Save
                    </Button>
                </Box>
            </Dialog>
        </Box>
    );
};

export { FormCreator };
export default FormCreator;
